import axios from 'axios';
import Cookies from 'js-cookie';
import { publicApi } from './instances';
import { getTokenExp } from '@/utils/jwt';
import { removeToken, setToken } from '@/utils/auth';

const adminApi = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_ADMIN_URL || '/api/admin',
  withCredentials: true,
});

adminApi.interceptors.request.use(async (config) => {
  if (typeof window !== 'undefined') {
    const token = Cookies.get('access_token');
    if (token) {
      const exp = getTokenExp(token);
      // Если токен истекает через 2 минуты или уже истек
      if (exp && (Date.now() > exp - 2 * 60 * 1000)) {
        const newToken = await proactiveRefreshToken();
        if (newToken) {
          Cookies.set('access_token', newToken, { path: '/', secure: true, sameSite: 'strict' });
          config.headers.Authorization = `Bearer ${newToken}`;
        }
      } else {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  }
  return config;
});

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

adminApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== '/auth/refresh-tokens'
    ) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (token) originalRequest.headers.Authorization = `Bearer ${token}`;
            return adminApi(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const res = await publicApi.post('/auth/refresh-tokens');
        const { access_token } = res.data;
        if (access_token) {
          setToken(access_token);

          adminApi.defaults.headers.Authorization = `Bearer ${access_token}`;
          processQueue(null, access_token);
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return adminApi(originalRequest);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        removeToken();
        // Централизованный logout через window
        if (typeof window !== 'undefined' && (window as any).__adminLogout) {
          (window as any).__adminLogout();
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

async function proactiveRefreshToken() {
  removeToken();
  try {
    const res = await publicApi.post('/auth/refresh-tokens');
    const { access_token } = res.data;
    if (access_token) {
      setToken(access_token);
      adminApi.defaults.headers.Authorization = `Bearer ${access_token}`;
      return access_token;
    }
  } catch {
    if (typeof window !== 'undefined' && (window as any).__adminLogout) {
      (window as any).__adminLogout();
    }
    return null;
  }
}

export default adminApi;